// The app shell: the header with the view and audio controls, the stage (the
// main view), the column of side panels and the control dock along the bottom.
// Features from the registry mount into the stage and the panel column; a panel's
// heading collapses it, and which panels are collapsed is part of the view state.
//
//   header:  title · view picker · Copy link · Start audio
//   stage                                  | panels
//   dock:    ignition · starter · throttle · engine speed · volume

import { features, type Feature, type Slot, type Unmount } from "../registry";
import type { ViewStore } from "../state/store";
import type { AudioSession } from "./controls/audio-session";
import { el } from "./controls/dom";
import { mountControls, type Controls } from "./controls";
import { strings } from "./strings";

export interface ShellContext {
  store: ViewStore;
  audio: AudioSession;
}

export interface Shell {
  slots: Record<Slot, HTMLElement>;
  controls: Controls;
  unmount(): void;
}

export function buildShell(app: HTMLElement, ctx: ShellContext, list: readonly Feature[] = features): Shell {
  const headerControls = el("div", { class: "header-controls" });
  const header = el(
    "header",
    { class: "app-header" },
    el("a", { class: "skip-link", href: "#dock" }, strings.skip),
    el("h1", { class: "app-title" }, strings.title),
    headerControls,
  );
  const stage = el("main", { class: "slot-stage", "data-slot": "stage", "aria-label": strings.stage });
  const panel = el("aside", { class: "slot-panel", "data-slot": "panel", "aria-label": strings.panels });
  const dock = el("footer", { class: "app-dock", id: "dock", tabindex: -1 });
  app.replaceChildren(header, stage, panel, dock);

  const controls = mountControls({ store: ctx.store, audio: ctx.audio, header: headerControls, dock });
  const slots: Record<Slot, HTMLElement> = { stage, panel };
  const unmounts: Unmount[] = [];
  for (const feature of list) {
    const unmount = mountFeature(feature, slots[feature.slot], ctx);
    if (unmount) unmounts.push(unmount);
  }
  return {
    slots,
    controls,
    unmount() {
      for (const u of unmounts.reverse()) u();
      controls.unmount();
    },
  };
}

function mountFeature(feature: Feature, slot: HTMLElement, ctx: ShellContext): Unmount | undefined {
  const root = el("section", { class: "feature", "data-feature": feature.id, "aria-label": feature.title });
  let body = root;
  const offs: Unmount[] = [];
  if (feature.slot === "panel") {
    // The heading is a button that collapses the panel (WAI-ARIA disclosure pattern).
    body = el("div", { class: "feature-body", id: `panel-${feature.id}` });
    const toggle = el("button", { type: "button", class: "panel-toggle", "aria-controls": body.id }, feature.title);
    toggle.addEventListener("click", () => {
      ctx.store.update((s) => {
        const collapsed = s.panels.collapsed.filter((id) => id !== feature.id);
        if (!isCollapsed(ctx.store, feature.id)) collapsed.push(feature.id);
        s.panels.collapsed = collapsed;
      });
    });
    const render = (): void => {
      const collapsed = isCollapsed(ctx.store, feature.id);
      toggle.setAttribute("aria-expanded", String(!collapsed));
      toggle.title = collapsed ? strings.expand : strings.collapse;
      body.hidden = collapsed;
      root.dataset["collapsed"] = String(collapsed);
    };
    offs.push(ctx.store.subscribe(render));
    root.append(el("h2", {}, toggle), body);
    render();
  }
  slot.append(root);
  try {
    const unmount = feature.mount({ root: body, state: ctx.store, audio: ctx.audio });
    if (unmount) offs.push(unmount);
  } catch (error) {
    // One broken feature must not take the others down.
    console.error(`Feature ${feature.id} failed to mount`, error);
    root.dataset["error"] = String(error);
    body.append(el("p", { class: "panel-note" }, strings.featureFailed));
  }
  return () => {
    for (const off of offs) off();
  };
}

function isCollapsed(store: ViewStore, id: string): boolean {
  return store.state.panels.collapsed.includes(id);
}
